import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card } from "@mui/material";

import { brandLogos } from "../assets/catalog";

// Two counter-scrolling rows. Split rather than hard-coded so adding a brand to
// the manifest lands in the marquee without touching this file.
const half = Math.ceil(brandLogos.length / 2);
const row1 = brandLogos.slice(0, half);
const row2 = brandLogos.slice(half);

const BrandCard = ({ logo, name, onClick }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      height: 120,
      minWidth: 200,

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      backgroundColor: "transparent",

      transition: "0.3s",
      cursor: "pointer",

      "&:hover img": {
        transform: "scale(1.08)",
      },
    }}
  >
    <Box
      component="img"
      src={logo}
      alt={`${name} logo`}
      sx={{
        maxWidth: 170,
        maxHeight: 76,
        objectFit: "contain",
        transition: "0.3s",
      }}
    />
  </Card>
);

// Duration scales with the row length so the scroll speed stays constant no
// matter how many brands the manifest carries.
const marqueeStyle = (direction = "left", count = 5) => ({
  display: "flex",
  gap: 10,

  animation: `${direction === "left"
    ? "marqueeLeft"
    : "marqueeRight"
    } ${count * 3}s linear infinite`,
});

const Brands = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: 6,
        background: "linear-gradient(180deg, #eef6ee 0%, #e4f0e3 60%, #f7faf7 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Blobs */}
      <Box sx={{
        position: "absolute", borderRadius: "50%", filter: "blur(100px)", pointerEvents: "none",
        width: { xs: 240, md: 480 }, height: { xs: 240, md: 480 },
        top: -160, right: -100, background: "rgba(98,129,65,0.10)",
      }} />
      <Box sx={{
        position: "absolute", borderRadius: "50%", filter: "blur(100px)", pointerEvents: "none",
        width: { xs: 160, md: 320 }, height: { xs: 160, md: 320 },
        bottom: -80, left: -60, background: "rgba(139,174,102,0.10)",
      }} />

      {/* Grid overlay */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(98,129,65,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(98,129,65,0.03) 1px, transparent 1px)",
        backgroundSize: "52px 52px",
      }} />
      {/* SAME WIDTH AS FEATURED COLLECTION */}
      <Box
        sx={{
          width: "85%",
          mx: "auto",

          px: {
            xs: "20px",
            md: "40px",
          },
        }}
      >
        {/* Header */}
        <Box mb={{ xs: 4, md: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              mb: 1.2,
            }}
          >
            <Box
              sx={{
                width: "28px",
                height: "2px",
                bgcolor: "#628141",
                borderRadius: "2px",
              }}
            />

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: ".12em",
                textTransform: "uppercase",
                color: "#628141",
              }}
            >
              Our Partners
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily:
                "'Barlow Condensed', sans-serif",

              fontSize: {
                xs: "2rem",
                md: "2.7rem",
              },

              fontWeight: 800,
              color: "#111",

              lineHeight: 1.05,

              letterSpacing: "-0.02em",

              mb: 1,
            }}
          >
            Trusted{" "}
            <span style={{ color: "#628141" }}>
              Brands
            </span>
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "#5b5b5b",
              lineHeight: 1.7,
              maxWidth: "540px",
            }}
          >
            Global HVAC and refrigeration brands
            delivering professional-grade
            reliability and performance.
          </Typography>
        </Box>

        {/* Row 1 */}
        <Box sx={marqueeStyle("left", row1.length)}>
          {[...row1, ...row1].map(
            (brand, index) => (
              <BrandCard
                logo={brand.logo}
                name={brand.name}
                key={index}
                onClick={() =>
                  navigate(
                    `/shop-by-brand/${encodeURIComponent(brand.name)}`
                  )
                }
              />
            )
          )}
        </Box>

        {/* Row 2 */}
        <Box
          mt={3}
          sx={marqueeStyle("right", row2.length)}
        >
          {[...row2, ...row2].map(
            (brand, index) => (
              <BrandCard
                logo={brand.logo}
                name={brand.name}
                key={index}
                onClick={() =>
                  navigate(
                    `/shop-by-brand/${encodeURIComponent(brand.name)}`
                  )
                }
              />
            )
          )}
        </Box>

        {/* Keyframes */}
        <style>
          {`
            @keyframes marqueeLeft {
              0% {
                transform: translateX(0);
              }

              100% {
                transform: translateX(-50%);
              }
            }

            @keyframes marqueeRight {
              0% {
                transform: translateX(-50%);
              }

              100% {
                transform: translateX(0);
              }
            }
          `}
        </style>
      </Box>
    </Box>
  );
};

export default Brands;